import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Alert,
    AlertDescription,
    AlertTitle,
} from '../components/ui/alert';
import { toast } from 'sonner';
import {
    ClipboardList,
    Car,
    MapPin,
    Calendar,
    Clock,
    Send,
    Loader2,
    CheckCircle,
    AlertTriangle,
    Lock,
    Info,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIPOS_VEHICULO = [
    { codigo: 'camion', nombre: 'Camión' },
    { codigo: 'camioneta', nombre: 'Camioneta' },
    { codigo: 'bus', nombre: 'Bus' },
    { codigo: 'minibus', nombre: 'Minibús' },
    { codigo: 'automovil', nombre: 'Automóvil' },
    { codigo: 'maquinaria', nombre: 'Maquinaria' },
    { codigo: 'otro', nombre: 'Otro' },
];

export default function CompletarOrdenClientePage() {
    const { token } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [orden, setOrden] = useState(null);
    const [tiposServicio, setTiposServicio] = useState([]);
    
    // Form data
    const [formData, setFormData] = useState({
        vehiculo_patente: '',
        vehiculo_tipo: '',
        vehiculo_marca: '',
        vehiculo_modelo: '',
        vehiculo_anio: '',
        vehiculo_color: '',
        descripcion: '',
        fecha_preferida: '',
        hora_preferida: '',
        ubicacion_direccion: '',
        ubicacion_comuna: '',
        ubicacion_ciudad: '',
        ubicacion_latitud: null,
        ubicacion_longitud: null,
        ubicacion_referencias: '',
        observaciones_cliente: '',
    });

    useEffect(() => {
        loadOrden();
    }, [token]);

    const loadOrden = async () => {
        try {
            setLoading(true);
            const res = await axios.get(`${API_URL}/api/ordenes/cliente/orden/${token}`);
            setOrden(res.data);
            setTiposServicio(res.data.tipos_servicio || []);
            
            // Pre-llenar campos existentes
            setFormData(prev => ({
                ...prev,
                vehiculo_patente: res.data.vehiculo_patente || '',
                vehiculo_tipo: res.data.vehiculo_tipo || '',
                vehiculo_marca: res.data.vehiculo_marca || '',
                vehiculo_modelo: res.data.vehiculo_modelo || '',
                vehiculo_anio: res.data.vehiculo_anio?.toString() || '',
                vehiculo_color: res.data.vehiculo_color || '',
                descripcion: res.data.descripcion || '',
                ubicacion_direccion: res.data.ubicacion_servicio?.direccion || '',
                ubicacion_comuna: res.data.ubicacion_servicio?.comuna || '',
                ubicacion_ciudad: res.data.ubicacion_servicio?.ciudad || '',
                ubicacion_latitud: res.data.ubicacion_servicio?.latitud || null,
                ubicacion_longitud: res.data.ubicacion_servicio?.longitud || null,
                ubicacion_referencias: res.data.ubicacion_servicio?.referencias || '',
            }));
        } catch (err) {
            console.error('Error loading orden:', err);
            setError(err.response?.data?.detail || 'Enlace no válido o expirado');
        } finally {
            setLoading(false);
        }
    };

    const isFieldLocked = (fieldName) => {
        return orden?.campos_bloqueados?.includes(fieldName);
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleGetLocation = () => {
        if (!navigator.geolocation) {
            toast.error('Geolocalización no soportada en este navegador');
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (position) => {
                setFormData(prev => ({
                    ...prev,
                    ubicacion_latitud: position.coords.latitude,
                    ubicacion_longitud: position.coords.longitude,
                }));
                toast.success('Ubicación obtenida');
            },
            (error) => {
                toast.error('No se pudo obtener la ubicación');
            }
        );
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validación básica
        if (!formData.ubicacion_direccion) {
            toast.error('La dirección del servicio es requerida');
            return;
        }

        setSubmitting(true);
        try {
            const payload = {
                vehiculo_patente: formData.vehiculo_patente || null,
                vehiculo_tipo: formData.vehiculo_tipo || null,
                vehiculo_marca: formData.vehiculo_marca || null,
                vehiculo_modelo: formData.vehiculo_modelo || null,
                vehiculo_anio: formData.vehiculo_anio ? parseInt(formData.vehiculo_anio) : null,
                vehiculo_color: formData.vehiculo_color || null,
                descripcion: formData.descripcion || null,
                fecha_preferida: formData.fecha_preferida || null,
                hora_preferida: formData.hora_preferida || null,
                ubicacion_servicio: {
                    direccion: formData.ubicacion_direccion,
                    comuna: formData.ubicacion_comuna || null,
                    ciudad: formData.ubicacion_ciudad || null,
                    latitud: formData.ubicacion_latitud,
                    longitud: formData.ubicacion_longitud,
                    referencias: formData.ubicacion_referencias || null,
                },
                observaciones_cliente: formData.observaciones_cliente || null,
            };

            await axios.post(`${API_URL}/api/ordenes/cliente/orden/${token}/completar`, payload);
            setSuccess(true);
            toast.success('Orden completada exitosamente');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al enviar');
        } finally {
            setSubmitting(false);
        }
    };

    const getTipoServicioNombre = (codigo) => {
        const tipo = tiposServicio.find(t => t.codigo === codigo);
        return tipo?.nombre || codigo;
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <Card className="w-full max-w-2xl">
                    <CardContent className="p-8">
                        <Skeleton className="h-8 w-64 mb-4" />
                        <Skeleton className="h-4 w-full mb-2" />
                        <Skeleton className="h-4 w-3/4 mb-8" />
                        <Skeleton className="h-48 w-full" />
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <Card className="w-full max-w-md">
                    <CardContent className="p-8 text-center">
                        <AlertTriangle className="h-16 w-16 text-red-500 mx-auto mb-4" />
                        <h2 className="text-xl font-bold mb-2">Enlace no válido</h2>
                        <p className="text-muted-foreground">{error}</p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (success) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <Card className="w-full max-w-md">
                    <CardContent className="p-8 text-center">
                        <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                        <h2 className="text-xl font-bold mb-2">¡Gracias!</h2>
                        <p className="text-muted-foreground mb-4">
                            Su información ha sido recibida exitosamente.
                        </p>
                        <p className="text-sm text-muted-foreground">
                            Número de orden: <strong>{orden?.numero_orden}</strong>
                        </p>
                        <p className="text-sm text-muted-foreground mt-4">
                            Nos pondremos en contacto con usted para coordinar el servicio.
                        </p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-8 px-4">
            <div className="max-w-2xl mx-auto">
                {/* Header */}
                <div className="text-center mb-8">
                    <h1 className="text-2xl font-bold flex items-center justify-center gap-2">
                        <ClipboardList className="h-7 w-7" />
                        Complete su Orden de Servicio
                    </h1>
                    <p className="text-muted-foreground mt-2">
                        Por favor complete los datos faltantes para procesar su solicitud
                    </p>
                </div>

                {/* Info de la orden */}
                <Card className="mb-6 border-2 border-blue-200 bg-blue-50">
                    <CardContent className="p-4">
                        <div className="flex flex-wrap gap-4 text-sm">
                            <div>
                                <span className="text-muted-foreground">Orden:</span>
                                <span className="font-bold ml-1">{orden?.numero_orden}</span>
                            </div>
                            <div>
                                <span className="text-muted-foreground">Cliente:</span>
                                <span className="font-medium ml-1">{orden?.cliente?.razon_social}</span>
                            </div>
                            <div>
                                <span className="text-muted-foreground">Servicio:</span>
                                <span className="font-medium ml-1">{getTipoServicioNombre(orden?.tipo_servicio)}</span>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <form onSubmit={handleSubmit}>
                    {/* Datos del Vehículo */}
                    <Card className="mb-6 border-2">
                        <CardHeader>
                            <CardTitle className="text-lg flex items-center gap-2">
                                <Car className="h-5 w-5" />
                                Datos del Vehículo
                            </CardTitle>
                            <CardDescription>
                                Información del vehículo donde se realizará el servicio
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                        Patente
                                        {isFieldLocked('vehiculo_patente') && <Lock className="h-3 w-3 text-muted-foreground" />}
                                    </Label>
                                    <Input
                                        value={formData.vehiculo_patente}
                                        onChange={(e) => handleChange('vehiculo_patente', e.target.value.toUpperCase())}
                                        placeholder="ABCD-12"
                                        disabled={isFieldLocked('vehiculo_patente')}
                                        className={isFieldLocked('vehiculo_patente') ? 'bg-muted' : ''}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Tipo de Vehículo</Label>
                                    <Select 
                                        value={formData.vehiculo_tipo || "_none"} 
                                        onValueChange={(v) => handleChange('vehiculo_tipo', v === "_none" ? "" : v)}
                                        disabled={isFieldLocked('vehiculo_tipo')}
                                    >
                                        <SelectTrigger className={isFieldLocked('vehiculo_tipo') ? 'bg-muted' : ''}>
                                            <SelectValue placeholder="Seleccionar" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="_none">Seleccionar...</SelectItem>
                                            {TIPOS_VEHICULO.map(t => (
                                                <SelectItem key={t.codigo} value={t.codigo}>{t.nombre}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                        Marca
                                        {isFieldLocked('vehiculo_marca') && <Lock className="h-3 w-3 text-muted-foreground" />}
                                    </Label>
                                    <Input
                                        value={formData.vehiculo_marca}
                                        onChange={(e) => handleChange('vehiculo_marca', e.target.value)}
                                        placeholder="Ej: Toyota"
                                        disabled={isFieldLocked('vehiculo_marca')}
                                        className={isFieldLocked('vehiculo_marca') ? 'bg-muted' : ''}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                        Modelo
                                        {isFieldLocked('vehiculo_modelo') && <Lock className="h-3 w-3 text-muted-foreground" />}
                                    </Label>
                                    <Input
                                        value={formData.vehiculo_modelo}
                                        onChange={(e) => handleChange('vehiculo_modelo', e.target.value)}
                                        placeholder="Ej: Hilux"
                                        disabled={isFieldLocked('vehiculo_modelo')}
                                        className={isFieldLocked('vehiculo_modelo') ? 'bg-muted' : ''}
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Año</Label>
                                    <Input
                                        type="number"
                                        value={formData.vehiculo_anio}
                                        onChange={(e) => handleChange('vehiculo_anio', e.target.value)}
                                        placeholder="2024"
                                        min="1900"
                                        max="2030"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Color</Label>
                                    <Input
                                        value={formData.vehiculo_color}
                                        onChange={(e) => handleChange('vehiculo_color', e.target.value)}
                                        placeholder="Ej: Blanco"
                                    />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Ubicación del Servicio */}
                    <Card className="mb-6 border-2 border-orange-200">
                        <CardHeader>
                            <CardTitle className="text-lg flex items-center gap-2">
                                <MapPin className="h-5 w-5 text-orange-500" />
                                Ubicación del Servicio *
                            </CardTitle>
                            <CardDescription>
                                ¿Dónde se realizará el trabajo?
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <Label>Dirección *</Label>
                                <Input
                                    value={formData.ubicacion_direccion}
                                    onChange={(e) => handleChange('ubicacion_direccion', e.target.value)}
                                    placeholder="Av. Principal 123"
                                    required
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Comuna</Label>
                                    <Input
                                        value={formData.ubicacion_comuna}
                                        onChange={(e) => handleChange('ubicacion_comuna', e.target.value)}
                                        placeholder="Santiago"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Ciudad</Label>
                                    <Input
                                        value={formData.ubicacion_ciudad}
                                        onChange={(e) => handleChange('ubicacion_ciudad', e.target.value)}
                                        placeholder="Santiago"
                                    />
                                </div>
                            </div>
                            
                            {/* Coordenadas GPS */}
                            <div className="p-4 bg-muted rounded-lg">
                                <div className="flex items-center justify-between mb-2">
                                    <Label className="text-sm">Coordenadas GPS (opcional)</Label>
                                    <Button type="button" variant="outline" size="sm" onClick={handleGetLocation}>
                                        <MapPin className="h-4 w-4 mr-1" />
                                        Obtener mi ubicación
                                    </Button>
                                </div>
                                {formData.ubicacion_latitud && formData.ubicacion_longitud ? (
                                    <div className="text-sm text-muted-foreground">
                                        <span className="font-mono">
                                            {formData.ubicacion_latitud.toFixed(6)}, {formData.ubicacion_longitud.toFixed(6)}
                                        </span>
                                        <a 
                                            href={`https://www.google.com/maps?q=${formData.ubicacion_latitud},${formData.ubicacion_longitud}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="ml-2 text-blue-500 underline"
                                        >
                                            Ver en mapa
                                        </a>
                                    </div>
                                ) : (
                                    <p className="text-xs text-muted-foreground">
                                        Haga clic en el botón para obtener sus coordenadas actuales
                                    </p>
                                )}
                            </div>

                            <div className="space-y-2">
                                <Label>Referencias adicionales</Label>
                                <Textarea
                                    value={formData.ubicacion_referencias}
                                    onChange={(e) => handleChange('ubicacion_referencias', e.target.value)}
                                    placeholder="Ej: Portón azul, al lado de la bencinera, preguntar por Juan..."
                                    rows={2}
                                />
                            </div>
                        </CardContent>
                    </Card>

                    {/* Preferencias de fecha/hora */}
                    <Card className="mb-6 border-2">
                        <CardHeader>
                            <CardTitle className="text-lg flex items-center gap-2">
                                <Calendar className="h-5 w-5" />
                                Preferencias de Fecha y Hora
                            </CardTitle>
                            <CardDescription>
                                Indique su disponibilidad (sujeto a confirmación)
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Fecha preferida</Label>
                                    <Input
                                        type="date"
                                        value={formData.fecha_preferida}
                                        onChange={(e) => handleChange('fecha_preferida', e.target.value)}
                                        min={new Date().toISOString().split('T')[0]}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Hora preferida</Label>
                                    <Select value={formData.hora_preferida || "_none"} onValueChange={(v) => handleChange('hora_preferida', v === "_none" ? "" : v)}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Seleccionar" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="_none">Cualquier hora</SelectItem>
                                            <SelectItem value="08:00-10:00">08:00 - 10:00</SelectItem>
                                            <SelectItem value="10:00-12:00">10:00 - 12:00</SelectItem>
                                            <SelectItem value="12:00-14:00">12:00 - 14:00</SelectItem>
                                            <SelectItem value="14:00-16:00">14:00 - 16:00</SelectItem>
                                            <SelectItem value="16:00-18:00">16:00 - 18:00</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Observaciones */}
                    <Card className="mb-6 border-2">
                        <CardHeader>
                            <CardTitle className="text-lg flex items-center gap-2">
                                <Info className="h-5 w-5" />
                                Observaciones Adicionales
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <Textarea
                                value={formData.observaciones_cliente}
                                onChange={(e) => handleChange('observaciones_cliente', e.target.value)}
                                placeholder="Cualquier información adicional que desee agregar..."
                                rows={3}
                            />
                        </CardContent>
                    </Card>

                    {/* Alerta */}
                    <Alert className="mb-6">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertTitle>Importante</AlertTitle>
                        <AlertDescription>
                            Una vez que envíe este formulario, no podrá modificar la información.
                            Asegúrese de que todos los datos sean correctos.
                        </AlertDescription>
                    </Alert>

                    {/* Submit */}
                    <Button type="submit" className="w-full h-12" disabled={submitting}>
                        {submitting ? (
                            <>
                                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                                Enviando...
                            </>
                        ) : (
                            <>
                                <Send className="h-5 w-5 mr-2" />
                                Enviar Información
                            </>
                        )}
                    </Button>
                </form>

                {/* Footer */}
                <p className="text-center text-xs text-muted-foreground mt-8">
                    Servicios 4Sat • Sistema de Órdenes de Servicio
                </p>
            </div>
        </div>
    );
}
